// Tipos mirror do backend (camelCase, como o ASP.NET serializa):
// - backend/Models/Postgres/ConferenciaNfe.cs, ConferenciaNfeItem.cs, ConferenciaNfeAnexo.cs
// - backend/Services/Conferencia/ConferenciaNfeService.cs (ConferenciaListItemDto, ConferenciaNfeFotoDto,
//   ListarConferenciasFiltroDto)
// - backend/Services/Conferencia/ResumoDivergenciaConferencia.cs (ResumoDivergencia)
// - backend/Services/Conferencia/StatusItemConferencia.cs (valores do enum-string de item)
// - backend/Controllers/ConferenciaNfeController.cs (request bodies + endpoint de barras)

/** ConferenciaNfe.Status — só estes dois valores, sem "cancelada" (ver service). */
export type StatusConferencia = 'em_andamento' | 'concluida';

/** ConferenciaNfe.Fonte — de onde a NF-e foi resolvida (ResolvedorNfeConferencia). */
export type FonteConferencia = 'erp' | 'manifesto';

/** StatusItemConferencia.Calcular — sempre recalculado no backend, nunca aceito do caller. */
export type StatusItemConferencia = 'pendente' | 'ok' | 'falta' | 'excedente' | 'avaria';

/**
 * Uma embalagem bipável do item (ConferenciaNfeItemEmbalagem no backend). O ERP guarda cada
 * embalagem do mesmo produto físico como uma linha de TESTPRODUTOGERAL com o mesmo
 * PRODUTOPRINCIPAL — 000428 "UN" ×1 e 000819 "CX" ×12 são a mesma trena, cada uma com seu
 * CODIGOBARRA. Bipar qualquer um casa o mesmo item; o que muda é quanto soma.
 */
export interface ConferenciaNfeItemEmbalagem {
  id: number;
  itemId: number;
  /** Código da variante em TESTPRODUTOGERAL (não o principal). */
  produto: string | null;
  ean: string | null;
  /** Unidades base que 1 leitura deste EAN soma. */
  qtdEmbalagem: number;
  /** Rótulo: "UN", "CX", "FD"… */
  unidade: string | null;
}

export interface ConferenciaNfeItem {
  id: number;
  conferenciaId: number;
  /**
   * PRODUTOPRINCIPAL da família (o backend consolida as linhas da nota por ele);
   * null se o EAN da NF-e não casou com TESTPRODUTOGERAL.
   */
  produto: string | null;
  descricao: string | null;
  ean: string | null;
  /** Sempre em unidades base do produto principal. */
  qtdNota: number;
  /** Unidades por embalagem do produto DA LINHA — só pra exibir "60 un (5 CX)". */
  qtdEmbalagem: number;
  unidade: string | null;
  qtdConferida: number;
  qtdAvariada: number;
  status: StatusItemConferencia;
  observacao: string | null;
  /** Todas as embalagens da família — o mapa EAN → multiplicador da bipagem. */
  embalagens: ConferenciaNfeItemEmbalagem[];
  /**
   * Só na resposta de um PUT: o servidor recusou a contagem por estourar a nota e zerou
   * `qtdConferida` (ver LimiteConferencia no backend). Não é estado persistido do item.
   */
  excedeuLimite?: boolean;
}

/** ResumoDivergenciaConferencia.Calcular — agregado dos status dos itens. */
export interface ResumoDivergencia {
  total: number;
  conferidos: number;
  pendentes: number;
  falta: number;
  excedente: number;
  avaria: number;
}

/** Chaves de categoria de pendência — espelho de backend/Services/Conferencia/CategoriasPendencia.cs. */
export type CategoriaPendencia = 'avaria' | 'excesso' | 'falta_volumes' | 'divergencia_qtd' | 'outro';

/** Rótulos (o backend valida a chave; os rótulos vivem só aqui). */
export const CATEGORIAS_PENDENCIA: { chave: CategoriaPendencia; label: string }[] = [
  { chave: 'avaria', label: 'Avaria' },
  { chave: 'excesso', label: 'Excesso' },
  { chave: 'falta_volumes', label: 'Falta de volumes' },
  { chave: 'divergencia_qtd', label: 'Divergência de quantidade' },
  { chave: 'outro', label: 'Outro' },
];

/** Detalhe completo (cabeçalho + itens) — GET/POST/concluir/reabrir/atualizar-cabeçalho devolvem isto. */
export interface ConferenciaNfe {
  id: number;
  empresa: number;
  chaveNfe: string;
  numeroNfe: string | null;
  serieNfe: string | null;
  fornecedorCnpj: string | null;
  fornecedorNome: string | null;
  fonte: FonteConferencia;
  setorRecebimento: string | null;
  status: StatusConferencia;
  observacao: string | null;
  usuario: string | null;
  valorTotalNf: number | null;
  criadoEm: string;
  concluidoEm: string | null;
  itens: ConferenciaNfeItem[];
  /**
   * Enriquecido de `Parametros.ConferenciaContagemManual` ([NotMapped] em ConferenciaNfe.cs —
   * mesmo molde de XmlTerceiro.ErpStatus/EmTransito). Gateia se o operador da doca pode
   * ajustar a contagem à mão (stepper/input) no workspace, ou só por bipagem.
   */
  contagemManualPermitida: boolean;
  /**
   * Enriquecido de `Parametros.ConferenciaMultiplicadorAtivo` ([NotMapped]). Liga o atalho
   * "N*código" na bipagem — multiplica a embalagem lida (ex.: 12*<EAN> soma 12×).
   */
  multiplicadorAtivo: boolean;
  /** Chaves de CATEGORIAS_PENDENCIA unidas por vírgula (ex.: "avaria,falta_volumes"); null = sem pendência declarada. */
  pendenciaCategorias: string | null;
  pendenciaObservacao: string | null;
  /** ISO; null = pendência ainda não resolvida (ou não há pendência). */
  pendenciaResolvidaEm: string | null;
  pendenciaResolvidaPor: string | null;
  pendenciaResolvidaObservacao: string | null;
  /** Derivado no backend ([NotMapped] ConferenciaNfe.TemPendencia): `pendenciaCategorias` não-vazio. */
  temPendencia: boolean;
  /** Derivado no backend ([NotMapped] ConferenciaNfe.PendenciaResolvida): `pendenciaResolvidaEm != null`. */
  pendenciaResolvida: boolean;
}

/** Linha de listagem (ConferenciaListItemDto) — cabeçalho + resumo, sem os itens completos. */
export interface ConferenciaListItem {
  id: number;
  empresa: number;
  chaveNfe: string;
  numeroNfe: string | null;
  serieNfe: string | null;
  fornecedorCnpj: string | null;
  fornecedorNome: string | null;
  fonte: FonteConferencia;
  setorRecebimento: string | null;
  status: StatusConferencia;
  usuario: string | null;
  valorTotalNf: number | null;
  criadoEm: string;
  concluidoEm: string | null;
  resumo: ResumoDivergencia;
  /**
   * Chaves de CATEGORIAS_PENDENCIA unidas por vírgula; null/"" = sem pendência declarada.
   * Sem derivado pronto do backend na LISTA (diferente do detalhe) — quem lê deriva:
   * tem pendência = não-vazio; resolvida = `pendenciaResolvidaEm != null`.
   */
  pendenciaCategorias: string | null;
  pendenciaResolvidaEm: string | null;
}

/** Metadados de uma foto anexada (ConferenciaNfeFotoDto) — sem os bytes. */
export interface ConferenciaNfeFoto {
  id: number;
  nomeArquivo: string | null;
  mime: string | null;
  tamanhoBytes: number;
  usuario: string | null;
  criadoEm: string;
}

/** Uma entrada da linha do tempo da pendência (ConferenciaNfeOcorrencia). Append-only. */
export interface OcorrenciaConferencia {
  id: number;
  conferenciaId: number;
  usuario: string;
  texto: string;
  /** manual | declarada | resolvida | reaberta */
  tipo: 'manual' | 'declarada' | 'resolvida' | 'reaberta';
  criadoEm: string;
}

/** Body de POST /conferencia-pendencia/{id}/ocorrencias. */
export interface RegistrarOcorrenciaRequest {
  texto: string;
}

/** Filtros de GET /api/conferencia-nfe (ListarConferenciasFiltroDto, via query string). */
export interface ListarConferenciasFiltros {
  status?: StatusConferencia;
  /** ISO date (yyyy-MM-dd ou yyyy-MM-ddTHH:mm:ss). */
  periodoIni?: string;
  periodoFim?: string;
  setor?: string;
  busca?: string;
  /** "com_pendencia" (tem e não resolvida) · "resolvida" · "sem" · omitido = todos. */
  pendencia?: 'com_pendencia' | 'resolvida' | 'sem';
}

/** Body de POST /api/conferencia-nfe (IniciarConferenciaRequest). */
export interface IniciarConferenciaRequest {
  empresa: number;
  chave: string;
}

/** Body de PUT /api/conferencia-nfe/{id}/item/{itemId} (AtualizarItemConferenciaRequest). */
export interface AtualizarItemConferenciaRequest {
  qtdConferida: number;
  qtdAvariada: number;
  observacao?: string | null;
  /**
   * Distingue a origem da mudança pro teto da nota (ver aplicarLimiteConferencia): omitido/false
   * = bipe (estourar zera e manda recontar); true = digitação que o operador confirmou acima da
   * nota (grava como excedente). Default false — um bipe nunca fura o teto por omissão.
   */
  permitirExcedente?: boolean;
}

/**
 * Body de PUT /api/conferencia-nfe/{id} (AtualizarCabecalhoConferenciaRequest). Update
 * PARCIAL: `null`/ausente em cada campo = "não altera este campo"; string (inclusive `""`)
 * = valor novo, `""` sendo um "limpar" legítimo. Cada commit do workspace manda só o campo
 * que editou — ver comentário de commitSetor/commitObservacao em ConferenciaWorkspacePage.
 */
export interface AtualizarCabecalhoConferenciaRequest {
  setorRecebimento?: string | null;
  observacao?: string | null;
}

/** Body de POST /api/conferencia-nfe/{id}/concluir. Corpo inteiro opcional — omitido = conclusão sem pendência. */
export interface ConcluirConferenciaRequest {
  pendenciaCategorias?: string[];
  pendenciaObservacao?: string | null;
}

/** Resultado de GET /api/conferencia-nfe/produto/barras/{ean} — objeto anônimo do controller. */
export interface ProdutoPorBarras {
  codigo: string;
  descricao: string | null;
  ean: string | null;
}
